import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { Toaster } from './components/ui/toaster';
import AnalyticsLoader from './components/AnalyticsLoader';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import { useThemeColors } from './hooks/useThemeColors';
import axios from 'axios';

// Public Pages
import Home from './pages/HomeNew';
import MovieDetail from './pages/MovieDetail';
import PersonMovies from './pages/PersonMovies';
import GenreMovies from './pages/GenreMovies';
import NotFound from './pages/NotFound';
import About from './pages/About';
import Branches from './pages/Branches';
import Contact from './pages/Contact';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Gallery from './pages/Gallery';
import News from './pages/News';
import EventDetail from './pages/EventDetail';
import CustomPage from './pages/CustomPage';
import NowPlaying from './pages/NowPlaying';
import Upcoming from './pages/Upcoming';

// Admin Pages
import AdminLogin from './components/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';
import AdminDashboard from './pages/AdminDashboard';
import AdminMovies from './pages/AdminMovies';
import AdminMovieForm from './pages/AdminMovieForm';
import AdminShowtimes from './pages/AdminShowtimes';
import AdminBranches from './pages/AdminBranches';
import AdminCampaigns from './pages/AdminCampaigns';
import AdminEvents from './pages/AdminEvents';
import AdminGallery from './pages/AdminGallery';
import AdminSettings from './pages/AdminSettings';
import AdminPages from './pages/AdminPages';
import AdminSmartRatings from './pages/AdminSmartRatings';
import AdminContactMessages from './pages/AdminContactMessages';
import AdminContactSubjects from './pages/AdminContactSubjects';
import AdminBiletinialSync from './pages/AdminBiletinialSync';
import AdminDatabaseExport from './pages/AdminDatabaseExport';
import AdminAnalytics from './pages/AdminAnalytics';
import SalonDetail from './pages/SalonDetail';

function App() {
  const [analyticsScript, setAnalyticsScript] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Load theme colors
  useThemeColors();

  useEffect(() => {
    // Load analytics script from settings
    const loadAnalytics = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/settings`);
        if (response.data && response.data.google_analytics_script) {
          setAnalyticsScript(response.data.google_analytics_script);
        }
      } catch (error) {
        console.error('Failed to load analytics:', error);
      }
    };
    loadAnalytics();
  }, [backendUrl]);

  return (
    <HelmetProvider>
      <SettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsLoader script={analyticsScript} />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            
            {/* Turkish Routes (Primary) */}
            <Route path="/vizyondaki-filmler" element={<NowPlaying />} />
            <Route path="/yakinda" element={<Upcoming />} />
            <Route path="/film/:slug" element={<MovieDetail />} />
            <Route path="/filmler/:id" element={<MovieDetail />} />
            <Route path="/kisi/:name" element={<PersonMovies />} />
            <Route path="/kampanyalar" element={<Campaigns />} />
            <Route path="/kampanyalar/:slug" element={<CampaignDetail />} />
            <Route path="/haberler" element={<News />} />
            <Route path="/haberler/:slug" element={<EventDetail />} />
            <Route path="/galeri" element={<Gallery />} />
            <Route path="/subelerimiz" element={<Branches />} />
            <Route path="/salonlarimiz" element={<Branches />} />
            <Route path="/salonlarimiz/:slug" element={<SalonDetail />} />
            <Route path="/salonlar/:slug" element={<SalonDetail />} />
            <Route path="/hakkimizda" element={<About />} />
            <Route path="/iletisim" element={<Contact />} />
            
            {/* English Routes (Backward Compatibility) */}
            <Route path="/now-playing" element={<NowPlaying />} />
            <Route path="/upcoming" element={<Upcoming />} />
            <Route path="/movies/:id" element={<MovieDetail />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/news" element={<News />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Custom Pages */}
            <Route path="/page/:slug" element={<CustomPage />} />
            
            {/* Static Pages - Direct URLs */}
            <Route path="/kariyer" element={<CustomPage />} />
            <Route path="/kvkk" element={<CustomPage />} />
            <Route path="/kullanim-kosullari" element={<CustomPage />} />
            <Route path="/gizlilik-politikasi" element={<CustomPage />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/movies"
              element={
                <ProtectedRoute>
                  <AdminMovies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/movies/new"
              element={
                <ProtectedRoute>
                  <AdminMovieForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/movies/edit/:id"
              element={
                <ProtectedRoute>
                  <AdminMovieForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/showtimes"
              element={
                <ProtectedRoute>
                  <AdminShowtimes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/branches"
              element={
                <ProtectedRoute>
                  <AdminBranches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/campaigns"
              element={
                <ProtectedRoute>
                  <AdminCampaigns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/events"
              element={
                <ProtectedRoute>
                  <AdminEvents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/gallery"
              element={
                <ProtectedRoute>
                  <AdminGallery />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/biletinial-sync"
              element={
                <ProtectedRoute>
                  <AdminBiletinialSync />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/database-export"
              element={
                <ProtectedRoute>
                  <AdminDatabaseExport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/smart-ratings"
              element={
                <ProtectedRoute>
                  <AdminSmartRatings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contact-messages"
              element={
                <ProtectedRoute>
                  <AdminContactMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contact-subjects"
              element={
                <ProtectedRoute>
                  <AdminContactSubjects />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/pages"
              element={
                <ProtectedRoute>
                  <AdminPages />
                </ProtectedRoute>
              }
            />
            <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
            
            {/* Genre Routes */}
            <Route path="/tur/:genre" element={<GenreMovies />} />
            
            {/* 404 - Must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieConsent />
          <Toaster />
        </BrowserRouter>
        </AuthProvider>
      </SettingsProvider>
    </HelmetProvider>
  );
}

export default App;
